// Number / currency formatting helpers
//
// MoneyFmt — display modes used across dashboards / data entry:
//   'compact'  → ₹2.18 Cr / ₹98.41 L  (default, original behavior)
//   'full'     → ₹2,18,45,000          (Indian comma grouping)
//   'lacs'     → ₹21,845 L             (always lacs, 2-decimal trimmed)

const fmtIndian = (n, digits = 0) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });

export const formatINR = (n, { compact = false, sign = false, mode = null } = {}) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  // Resolved display mode: explicit `mode` wins, else `compact` legacy flag
  const m = mode || (compact ? 'compact' : 'full');
  let out;
  if (m === 'compact') {
    const abs = Math.abs(n);
    if (abs >= 1e7) out = `${(n / 1e7).toFixed(2)} Cr`;
    else if (abs >= 1e5) out = `${(n / 1e5).toFixed(2)} L`;
    else out = fmtIndian(n);
  } else if (m === 'lacs') {
    // Show value as lacs with 2 decimals; e.g., 21,845.50 L
    const lacs = n / 1e5;
    out = `${fmtIndian(lacs, 2)} L`;
  } else {
    out = fmtIndian(n);
  }
  if (sign && n > 0) return `+₹${out}`;
  return `₹${out}`;
};

export const formatUSD = (n, { compact = false, sign = false } = {}) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  let out;
  if (compact) {
    if (abs >= 1e9) out = `${(n / 1e9).toFixed(2)}B`;
    else if (abs >= 1e6) out = `${(n / 1e6).toFixed(2)}M`;
    else if (abs >= 1e3) out = `${(n / 1e3).toFixed(1)}K`;
    else out = n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  } else {
    out = n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (sign && n > 0) return `+$${out}`;
  return `$${out}`;
};

// App is INR-only. `currency` arg kept for backwards compat but ignored.
// `opts` may include `mode: 'compact' | 'full' | 'lacs'` (preferred) or legacy `compact: true`.
export const formatCurrency = (n, _currency = 'INR', opts = {}) => formatINR(n, opts);

export const formatPct = (n, { digits = 2, sign = true } = {}) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const str = `${Math.abs(n).toFixed(digits)}%`;
  if (!sign) return str;
  if (n > 0) return `+${str}`;
  if (n < 0) return `-${str}`;
  return str;
};

export const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

export const growthPct = (curr, prev) => {
  if (prev === 0 || prev === null || prev === undefined) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};
